import { Copy, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

// Ile niedopasowanych KSID wypisujemy w toascie zanim skrócimy do licznika.
// Pełna lista i tak siedzi w panelu (z kopiowaniem), toast jest tylko sygnałem.
const UNMATCHED_PREVIEW_LIMIT = 20;

/**
 * Konfiguracja jednego rodzaju kodu w imporcie z pliku. Pola tekstowe idą wprost
 * do komunikatów, żeby panel i toasty mówiły językiem konkretnego modułu.
 */
export interface ImportCodeFormat {
	/** Nazwa kodu w komunikatach: "KSID", "BLOZ". */
	label: string;
	/** Opis poprawnego formatu: "5 lub 6 cyfr", "7 cyfr". */
	hint: string;
	/** Dopełniacz l. mnogiej dopasowanych rekordów: "aptek", "produktów". */
	itemNoun: string;
	/** Czemu kod mógł nie zostać dopasowany — nagłówek listy nie zaczytanych. */
	unmatchedHint: string;
	isValid: (code: string) => boolean;
}

/**
 * KSID to 5 albo 6 cyfr — `farm_firm.unitcode` trzyma kody BEZ wiodącego zera,
 * więc `70151` (F-Brzeźnica) jest równie poprawne jak `880390`. Dopełnianie
 * takiego kodu do sześciu znaków rozjechałoby go z bazą.
 */
export const KSID_FORMAT: ImportCodeFormat = {
	label: "KSID",
	hint: "5 lub 6 cyfr",
	itemNoun: "aptek",
	unmatchedHint: "brak apteki w FM2 lub poza uprawnieniami",
	isValid: (code) => /^\d{5,6}$/.test(code),
};

/**
 * BLOZ to dokładnie 7 cyfr — w `public.bloz` wszystkie 261 tys. kodów mają tę
 * długość i żaden nie zaczyna się od zera, więc nie ma tu wyjątku jak przy KSID.
 * Dopasowanie leci po `ftowr` (kartoteka org), nie po słowniku — poprawny kod
 * spoza kartoteki wróci jako nie zaczytany i to jest prawdziwa informacja.
 */
export const BLOZ_FORMAT: ImportCodeFormat = {
	label: "BLOZ",
	hint: "7 cyfr",
	itemNoun: "produktów",
	unmatchedHint: "brak produktu w kartotece towarów",
	isValid: (code) => /^\d{7}$/.test(code),
};

/** Separatory kolumn w plikach tekstowych/CSV. */
const TEXT_COLUMN_SEPARATOR = /[;,\t]/;

export interface SingleColumnExtraction {
	/**
	 * Ile kolumn ma plik. `> 1` = plik odrzucony, `codes` i `invalid` są puste —
	 * przy wielu kolumnach nie zgadujemy, która trzyma KSID.
	 */
	columns: number;
	/** Poprawne KSID w kolejności z pliku (z powtórzeniami). */
	codes: string[];
	/** Wartości odrzucone przed wysłaniem — zły format, nie da się ich dopasować. */
	invalid: string[];
}

/** Liczba kolumn = najdalej wysunięta niepusta komórka w którymkolwiek wierszu. */
function countColumns(rows: string[][]): number {
	let columns = 0;
	for (const row of rows) {
		for (let i = row.length - 1; i >= 0; i--) {
			if (row[i] !== "") {
				columns = Math.max(columns, i + 1);
				break;
			}
		}
	}
	return columns;
}

/**
 * Dzieli wartości pierwszej kolumny na poprawne KSID i odrzucone.
 *
 * Nagłówek (pierwszy wiersz bez cyfr, np. "KSID") jest pomijany — reszta:
 * cokolwiek niepustego, co nie pasuje do formatu kodu, ląduje w `invalid`.
 * Nic nie odsiewamy po cichu: wartość odrzucona trafia do panelu, żeby dało się
 * ją odczytać i poprawić.
 */
function splitFirstColumn(
	values: string[],
	format: ImportCodeFormat,
): Pick<SingleColumnExtraction, "codes" | "invalid"> {
	const codes: string[] = [];
	const invalid: string[] = [];

	values.forEach((value, index) => {
		if (value === "") return;
		if (format.isValid(value)) {
			codes.push(value);
			return;
		}
		if (index === 0 && !/\d/.test(value)) return; // nagłówek kolumny
		invalid.push(value);
	});

	return { codes, invalid };
}

/** Czyta pierwszą kolumnę arkusza (AOA z `sheet_to_json({header: 1})`). */
export function extractSingleColumnFromSheet(
	rows: unknown[][],
	format: ImportCodeFormat,
): SingleColumnExtraction {
	const cells = rows.map((row) => (row ?? []).map((cell) => String(cell ?? "").trim()));
	const columns = countColumns(cells);
	if (columns > 1) return { columns, codes: [], invalid: [] };

	return {
		columns,
		...splitFirstColumn(
			cells.map((row) => row[0] ?? ""),
			format,
		),
	};
}

/** Czyta pierwszą kolumnę pliku tekstowego/CSV — jedna wartość na linię. */
export function extractSingleColumnFromText(
	text: string,
	format: ImportCodeFormat,
): SingleColumnExtraction {
	const lines = text
		.split(/\r?\n/)
		.map((line) => line.split(TEXT_COLUMN_SEPARATOR).map((field) => field.trim()))
		.filter((fields) => fields.some((field) => field !== ""));
	const columns = countColumns(lines);
	if (columns > 1) return { columns, codes: [], invalid: [] };

	return {
		columns,
		...splitFirstColumn(
			lines.map((fields) => fields[0] ?? ""),
			format,
		),
	};
}

export interface ImportReconciliation {
	/** Liczba wszystkich kodów odczytanych z pliku (z powtórzeniami). */
	totalCodes: number;
	/** Liczba unikalnych kodów wysłanych do dopasowania. */
	requested: number;
	/** Liczba kodów, dla których backend zwrócił aptekę. */
	matched: number;
	/** Kody z pliku, których nie dopasowano do żadnej apteki. */
	unmatchedCodes: string[];
	/** Kody występujące w pliku więcej niż raz (każdy wymieniony raz). */
	duplicateCodes: string[];
	/** Ile wystąpień odpadło na deduplikacji (`totalCodes - requested`). */
	duplicatesRemoved: number;
	/** Wartości odrzucone przed wysłaniem, bo nie pasują do formatu kodu. */
	invalidCodes: string[];
}

/**
 * Porównuje kody zażądane z pliku z rekordami faktycznie zwróconymi przez
 * backend. Backend zwraca tylko trafienia (i tylko widoczne dla użytkownika),
 * więc różnica = kody, które cicho przepadały (nieistniejące lub poza
 * uprawnieniami). Rozdziela też ubytek na duplikaty w pliku vs brak rekordu w
 * bazie — bez tego użytkownik widzi tylko, że "zaznaczyło mniej", i nie wie czemu.
 */
export function reconcileCodeImport(
	requestedCodes: string[],
	matchedCodes: string[],
	invalidCodes: string[] = [],
): ImportReconciliation {
	const uniqueRequested = Array.from(new Set(requestedCodes));
	const seen = new Set<string>();
	const duplicates = new Set<string>();
	for (const code of requestedCodes) {
		if (seen.has(code)) duplicates.add(code);
		seen.add(code);
	}
	const matchedSet = new Set(matchedCodes);
	const unmatchedCodes = uniqueRequested.filter((code) => !matchedSet.has(code));
	return {
		totalCodes: requestedCodes.length,
		requested: uniqueRequested.length,
		matched: uniqueRequested.length - unmatchedCodes.length,
		unmatchedCodes,
		duplicateCodes: uniqueRequested.filter((code) => duplicates.has(code)),
		duplicatesRemoved: requestedCodes.length - uniqueRequested.length,
		invalidCodes: Array.from(new Set(invalidCodes)),
	};
}

/** Kody do schowka — jeden na linię, żeby wkleiły się jako kolumna w Excelu. */
export function formatUnmatchedForClipboard(codes: string[]): string {
	return codes.join("\n");
}

/**
 * Buduje treść toasta podsumowującego import z pliku. `warning` gdy cokolwiek
 * nie zostało dopasowane, w przeciwnym razie `success`. Zwraca czysty obiekt
 * (bez efektów ubocznych) — dyspozycję do `sonner` robi komponent.
 */
export function buildImportSummaryToast(
	result: Pick<ImportReconciliation, "requested" | "matched" | "unmatchedCodes"> & {
		invalidCodes?: string[];
	},
	duplicatesRemoved: number,
	format: ImportCodeFormat = KSID_FORMAT,
): { level: "success" | "warning"; message: string; description?: string } {
	const invalidCodes = result.invalidCodes ?? [];
	const notes: string[] = [];
	if (duplicatesRemoved > 0) {
		notes.push(`pominięto ${duplicatesRemoved} zdublowanych kodów w pliku`);
	}
	if (invalidCodes.length > 0) {
		const preview = invalidCodes.slice(0, UNMATCHED_PREVIEW_LIMIT).join(", ");
		const overflow = invalidCodes.length - UNMATCHED_PREVIEW_LIMIT;
		notes.push(
			`niepoprawny format (oczekiwane ${format.hint}): ${preview}${overflow > 0 ? ` … (+${overflow})` : ""}`,
		);
	}

	if (result.unmatchedCodes.length === 0) {
		if (invalidCodes.length > 0) {
			return {
				level: "warning",
				message: `Dopasowano ${result.matched} ${format.itemNoun} — ${invalidCodes.length} kodów odrzucono`,
				description: notes.join(". "),
			};
		}
		return {
			level: "success",
			message: `Dopasowano ${result.matched} ${format.itemNoun}`,
			description: notes.length > 0 ? notes.join("; ") : undefined,
		};
	}

	const preview = result.unmatchedCodes.slice(0, UNMATCHED_PREVIEW_LIMIT).join(", ");
	const overflow = result.unmatchedCodes.length - UNMATCHED_PREVIEW_LIMIT;
	const list = overflow > 0 ? `${preview} … (+${overflow})` : preview;

	return {
		level: "warning",
		message: `Dopasowano ${result.matched} z ${result.requested} kodów — ${result.unmatchedCodes.length} nie dopasowano`,
		description: [`Niedopasowane ${format.label}: ${list}`, ...notes].join(". "),
	};
}

async function copyCodes(codes: string[], label: string) {
	try {
		await navigator.clipboard.writeText(formatUnmatchedForClipboard(codes));
		toast.success(`Skopiowano ${codes.length} kodów (${label})`);
	} catch {
		toast.error("Nie udało się skopiować do schowka.");
	}
}

/** Lista kodów KSID z przyciskiem kopiowania — pełna, bez skracania. */
function CodeList({
	title,
	codes,
	copyLabel,
}: {
	title: string;
	codes: string[];
	copyLabel: string;
}) {
	return (
		<div className="space-y-1">
			<div className="flex items-center gap-2 max-md:flex-wrap">
				<span className="font-bold uppercase tracking-wide">{title}</span>
				<Button
					type="button"
					variant="outline"
					size="sm"
					className="h-6 gap-1 px-2 text-[10px] bg-background/60 max-md:h-9 max-md:text-xs"
					onClick={() => void copyCodes(codes, copyLabel)}
				>
					<Copy className="size-3" />
					Kopiuj
				</Button>
			</div>
			<div className="max-h-20 overflow-y-auto rounded-md border border-border bg-background/60 p-2 font-mono leading-5 break-all text-foreground">
				{codes.join(", ")}
			</div>
		</div>
	);
}

/**
 * Podsumowanie ostatniego importu kodów z pliku. Toast znika, a użytkownik musi
 * móc odczytać i skopiować kody, które nie weszły do zaznaczenia — inaczej widzi
 * tylko, że "zaczytało mniej", bez rozróżnienia dubli w pliku od braku rekordu w bazie.
 */
export function ImportReportPanel({
	report,
	format,
	onDismiss,
	className,
}: {
	report: ImportReconciliation;
	format: ImportCodeFormat;
	onDismiss: () => void;
	className?: string;
}) {
	const hasProblems = report.unmatchedCodes.length > 0 || report.invalidCodes.length > 0;

	return (
		<div
			className={cn(
				"border rounded-lg px-4 py-3 text-[11px] space-y-2",
				hasProblems
					? "bg-status-warning-bg border-status-warning-border text-status-warning-text"
					: "bg-status-success-bg border-status-success-border text-status-success-text",
				className,
			)}
		>
			<div className="flex items-start gap-3">
				<div className="flex-1 flex flex-wrap items-center gap-x-4 gap-y-1 font-medium">
					<span className="font-black uppercase tracking-wider">Import z pliku</span>
					<span>
						kody w pliku: <span className="font-bold tabular-nums">{report.totalCodes}</span>
					</span>
					<span>
						unikalne: <span className="font-bold tabular-nums">{report.requested}</span>
					</span>
					<span>
						duplikaty w pliku:{" "}
						<span className="font-bold tabular-nums">{report.duplicatesRemoved}</span>
					</span>
					<span>
						zaczytano: <span className="font-bold tabular-nums">{report.matched}</span>
					</span>
					<span>
						nie zaczytano:{" "}
						<span className="font-bold tabular-nums">{report.unmatchedCodes.length}</span>
					</span>
					{report.invalidCodes.length > 0 && (
						<span>
							niepoprawny format:{" "}
							<span className="font-bold tabular-nums">{report.invalidCodes.length}</span>
						</span>
					)}
				</div>
				<button
					type="button"
					onClick={onDismiss}
					className="opacity-60 hover:opacity-100 transition-opacity max-md:-m-2.5 max-md:p-2.5"
					aria-label="Zamknij podsumowanie importu"
				>
					<X className="size-4" />
				</button>
			</div>

			{report.invalidCodes.length > 0 && (
				<CodeList
					title={`Niepoprawny format kodu (oczekiwane ${format.hint}) — ${report.invalidCodes.length}`}
					codes={report.invalidCodes}
					copyLabel="niepoprawny format"
				/>
			)}

			{report.unmatchedCodes.length > 0 && (
				<CodeList
					title={`Nie zaczytane ${format.label} (${format.unmatchedHint}) — ${report.unmatchedCodes.length}`}
					codes={report.unmatchedCodes}
					copyLabel="nie zaczytane"
				/>
			)}

			{report.duplicateCodes.length > 0 && (
				<CodeList
					title={`Kody powtórzone w pliku — ${report.duplicateCodes.length}`}
					codes={report.duplicateCodes}
					copyLabel="duplikaty"
				/>
			)}
		</div>
	);
}
